import type { AuditTrailItem } from '../project/components/dashboard/auditTrail/auditTrailTypes';
import { getActorDisplay, looksLikeUuid } from '../project/components/dashboard/auditTrail/auditTrailConstants';
import { toAuditTrailUtcMillis } from './auditTrailDateTime';

export interface AuditSessionGroup {
  sessionId: string;
  events: AuditTrailItem[];
  startAt: string;
  endAt: string;
  actorLabel: string;
  actorSecondary?: string;
}

function actorLabelForSession(events: AuditTrailItem[]): { primary: string; secondary?: string } {
  for (const row of events) {
    const display = getActorDisplay(row);
    if (display.primary && display.primary !== '—' && !looksLikeUuid(display.primary)) {
      return display;
    }
  }
  for (const row of events) {
    const display = getActorDisplay(row);
    if (display.primary && display.primary !== '—') {
      return display;
    }
  }
  return { primary: 'Unknown user' };
}

export interface AuditActorGroup {
  actorKey: string;
  actorLabel: string;
  actorSecondary?: string;
  sessions: AuditSessionGroup[];
  totalEvents: number;
}

function getSessionActorIdentity(session: AuditSessionGroup): {
  groupKey: string;
  label: string;
  secondary?: string;
} {
  for (const row of session.events) {
    const email =
      row.actor_email?.trim() ||
      (row.actor_id?.includes('@') ? row.actor_id.trim() : '');
    if (email) {
      const name = row.actor_name?.trim();
      return {
        groupKey: email.toLowerCase(),
        label: email,
        secondary: name && !looksLikeUuid(name) && name !== email ? name : session.actorSecondary,
      };
    }
  }

  for (const row of session.events) {
    const id = row.actor_id?.trim();
    if (id && looksLikeUuid(id)) {
      return { groupKey: id, label: session.actorLabel, secondary: session.actorSecondary };
    }
  }

  return {
    groupKey: session.actorLabel,
    label: session.actorLabel,
    secondary: session.actorSecondary,
  };
}

/** Group login sessions under the same user (email preferred, else Cognito id). */
export function groupSessionsByActor(sessions: AuditSessionGroup[]): AuditActorGroup[] {
  const map = new Map<string, AuditActorGroup>();

  sessions.forEach((session) => {
    const identity = getSessionActorIdentity(session);
    const existing = map.get(identity.groupKey);

    if (!existing) {
      map.set(identity.groupKey, {
        actorKey: identity.groupKey,
        actorLabel: identity.label,
        actorSecondary: identity.secondary,
        sessions: [session],
        totalEvents: session.events.length,
      });
      return;
    }

    if (identity.label.includes('@') && !existing.actorLabel.includes('@')) {
      existing.actorLabel = identity.label;
      existing.actorSecondary = identity.secondary ?? existing.actorSecondary;
    }

    existing.sessions.push(session);
    existing.totalEvents += session.events.length;
  });

  const groups = Array.from(map.values());
  groups.forEach((group) => {
    group.sessions.sort((a, b) => toAuditTrailUtcMillis(b.startAt) - toAuditTrailUtcMillis(a.startAt));
  });

  return groups.sort(
    (a, b) => toAuditTrailUtcMillis(b.sessions[0]?.startAt ?? '') - toAuditTrailUtcMillis(a.sessions[0]?.startAt ?? '')
  );
}

/** Group chronological events (ASC) by session_id; events without session go in a synthetic bucket. */
export function groupAuditEventsBySession(items: AuditTrailItem[]): AuditSessionGroup[] {
  const map = new Map<string, AuditTrailItem[]>();

  for (const row of items) {
    const key = row.session_id?.trim() || '__no_session__';
    const list = map.get(key) ?? [];
    list.push(row);
    map.set(key, list);
  }

  const groups: AuditSessionGroup[] = [];

  map.forEach((events, sessionId) => {
    const sorted = [...events].sort((a, b) => toAuditTrailUtcMillis(a.created_at) - toAuditTrailUtcMillis(b.created_at));
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const actor = actorLabelForSession(sorted);
    groups.push({
      sessionId: sessionId === '__no_session__' ? '—' : sessionId,
      events: sorted,
      startAt: first?.created_at ?? '',
      endAt: last?.created_at ?? '',
      actorLabel: actor.primary,
      actorSecondary: actor.secondary,
    });
  });

  return groups.sort((a, b) => toAuditTrailUtcMillis(b.startAt) - toAuditTrailUtcMillis(a.startAt));
}
